import { yupResolver } from '@hookform/resolvers/yup'
import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import MaskedInput from 'components/UI/Inputs/MaskedInput'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import LoanCancelButton from 'components/views/Decision/LoanCancelBlock'
import { useAppSelector } from 'hooks'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import { useAddSpouseMutation } from 'redux/api/pledgeApi'
import { getActiveCreditApplication, getCurrentFlowId, getSpouseRecord } from 'redux/store/slices/userSlice'
import { ApplicationEvents } from 'share/enums/events'
import tw from 'twin.macro'
import Emitter from 'utils/emitter'
import { IINMask, PhoneMask } from 'utils/i-masks'
import { validateIIN } from 'utils/validateIIN'
import * as yup from 'yup'

type FormValues = {
  phone: string
  iin: string
}

type AddSpouseApiError = {
  data: {
    errors: string
    detail: string
  }
  status: number
}

const formSchema = yup.object().shape({
  phone: yup.string().min(11, 'create-lead-schema.phone-min').max(11).required(),
  iin: yup
    .string()
    .min(12, 'create-lead-schema.iin-min')
    .max(12)
    .test('checkIin', 'create-lead-schema.iin-validation', value => validateIIN(value))
    .required()
})

const SpouseConfirmation = () => {
  // Tools
  const { t } = useTranslation()

  // States
  const [isLoading, setIsLoading] = useState(false)

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { id: creditID } = useAppSelector(getActiveCreditApplication)
  const { iin, phone } = useAppSelector(getSpouseRecord)

  // Mutations
  const [fetchStatus] = useLazyGetApplicationStatusQuery()
  const [addSpouse] = useAddSpouseMutation()

  // Forms
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError
  } = useForm<FormValues>({
    defaultValues: {
      phone: '',
      iin: ''
    },
    resolver: yupResolver(formSchema)
  })

  // Handlers
  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true)
      if (!currentFlowId || !creditID) throw new Error('Flow ID or CreditID is not defined')
      const { current_step } = await fetchStatus(currentFlowId, false).unwrap()
      if (!current_step) throw new Error('Current step is not defined')
      await addSpouse({
        step_uuid: current_step.id,
        credit_application: creditID,
        iin: data.iin,
        mobile_phone: `+${data.phone}`
      }).unwrap()
      setTimeout(() => {
        setIsLoading(false)
        Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
      }, 350)
    } catch (error) {
      setIsLoading(false)
      const typedError = error as AddSpouseApiError
      typedError.data.errors && setError('iin', { message: typedError.data.detail, type: 'custom' })
      typedError.data.detail && setError('phone', { message: '', type: 'custom' })
    }
  }

  return (
    <ContainerWithSteps>
      <form tw='mt-4 flex flex-col gap-5 md:mt-0' onSubmit={handleSubmit(onSubmit)}>
        <InfoBlock twStyle={tw`flex flex-col gap-4`}>
          <Heading variant='body' style={{ textWrap: 'pretty' }}>
            {t('spouse-info')}
          </Heading>
          <Typography text variant='subbody' twStyle={tw`text-grey-dark`}>
            {t('not-discription')}
          </Typography>
          <div tw='flex gap-4 flex-wrap sm:flex-nowrap'>
            <MaskedInput
              t={t}
              label={t('flow-iin')}
              name='iin'
              disabled={isLoading}
              control={control}
              placeholder={t('flow-iin-placeholder')}
              maskOptions={IINMask}
            />
            <MaskedInput
              t={t}
              label={t('flow-mobile-number')}
              placeholder={'+7 (---) --- -- --'}
              name='phone'
              disabled={isLoading}
              control={control}
              maskOptions={{
                mask: PhoneMask
              }}
            />
          </div>
          <Alert>
            <Typography variant='subbody'>{t('if-not-married')}</Typography>
          </Alert>
          {/* <SpouseConfitmationState /> */}
        </InfoBlock>
        <div tw='px-4 flex flex-col justify-center items-center space-y-3 lg:px-0 lg:flex-row-reverse lg:space-y-0'>
          <Button
            type='submit'
            variant='primary'
            twStyle={tw`flex-1 ml-0 md:ml-5`}
            disabled={!isValid}
            loading={isLoading}
          >
            <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
              {t('flow-continue')}
            </Typography>
          </Button>
          <LoanCancelButton
            disabled={isValid || isLoading}
            buttonTextStyles={tw`text-red-light lg:whitespace-nowrap font-medium`}
          />
        </div>
      </form>
    </ContainerWithSteps>
  )
}

export default SpouseConfirmation
